import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { useLogFollowUp } from "@/api/followups/hooks";
import { toNoonUTC } from "@/lib/dateUtils";
import { Loader2 } from "lucide-react";

// ── Zod Schemas ──

const fullPaymentSchema = z
  .object({
    timingType: z.enum(["specific_month", "specific_week", "exact_date"]),
    month: z.string().optional(),
    week: z.coerce.number().min(1).max(4).optional(),
    exactDate: z.date().optional(),
    isTentative: z.boolean().default(false),
    paymentMethod: z.enum(["bank_transfer", "handover", "direct_to_office"]),
  })
  .refine(
    (data) => {
      if (data.timingType === "specific_month") return !!data.month;
      if (data.timingType === "specific_week") return !!data.week;
      if (data.timingType === "exact_date") return !!data.exactDate;
      return true;
    },
    { message: "Please fill in the required timing field." },
  );

const installmentSchema = z.object({
  installmentCount: z.union([z.coerce.number().min(1), z.literal("not_fixed")]),
  frequency: z.enum(["monthly", "bi_monthly", "custom"]),
  customFrequencyDays: z.coerce.number().min(1).optional(),
  startDate: z.date().refine((d) => d > new Date(), {
    message: "Start date must be in the future",
  }),
});

const flexibleSchema = z.object({
  nextCheckInDate: z.date().refine((d) => d > new Date(), {
    message: "Check-in date must be in the future",
  }),
});

const noContactSchema = z.object({
  callStatus: z.enum(["call_not_taken", "unreachable", "no_whatsapp_reply"]),
  actionTaken: z.enum(["left_voicemail", "sent_whatsapp", "will_retry"]),
});

const schemas = {
  full_payment: fullPaymentSchema,
  installment: installmentSchema,
  flexible: flexibleSchema,
  no_contact: noContactSchema,
};

const tabBanners = {
  full_payment: {
    bg: "bg-emerald-50 border-emerald-100 text-emerald-700",
    text: "Use when the sponsor commits to paying the full amount on a specific date.",
  },
  installment: {
    bg: "bg-purple-50 border-purple-100 text-purple-700",
    text: "Use when the sponsor wants to pay in multiple installments over time.",
  },
  flexible: {
    bg: "bg-yellow-50 border-yellow-100 text-yellow-700",
    text: "Use when the sponsor is interested but hasn't committed to a timeline yet.",
  },
  no_contact: {
    bg: "bg-orange-50 border-orange-100 text-orange-700",
    text: "Use when you couldn't reach the sponsor or they didn't respond.",
  },
};

// ── Tab Forms ──

function FullPaymentTab({ form }) {
  const timingType = form.watch("timingType");

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Timing Type</Label>
        <Controller
          name="timingType"
          control={form.control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value || ""}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select timing type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="specific_month">Specific Month</SelectItem>
                <SelectItem value="specific_week">Specific Week</SelectItem>
                <SelectItem value="exact_date">Exact Date</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {timingType === "specific_month" && (
        <div className="space-y-1.5">
          <Label className="text-xs">Month (e.g. March 2025)</Label>
          <Input
            {...form.register("month")}
            placeholder="March 2025"
            className="h-8 text-xs"
          />
        </div>
      )}

      {timingType === "specific_week" && (
        <div className="space-y-1.5">
          <Label className="text-xs">Week Number</Label>
          <Controller
            name="week"
            control={form.control}
            render={({ field }) => (
              <Select
                onValueChange={(v) => field.onChange(parseInt(v, 10))}
                value={field.value?.toString() || ""}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select week" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Week 1</SelectItem>
                  <SelectItem value="2">Week 2</SelectItem>
                  <SelectItem value="3">Week 3</SelectItem>
                  <SelectItem value="4">Week 4</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
      )}

      {timingType === "exact_date" && (
        <div className="space-y-1.5">
          <Label className="text-xs">Exact Date</Label>
          <Controller
            name="exactDate"
            control={form.control}
            render={({ field }) => (
              <DatePicker
                selected={field.value}
                onSelect={field.onChange}
                placeholder="Pick a date"
                className="w-full"
              />
            )}
          />
        </div>
      )}

      <div className="flex items-center space-x-2">
        <Controller
          name="isTentative"
          control={form.control}
          render={({ field }) => (
            <Checkbox
              id="isTentative"
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
        <Label htmlFor="isTentative" className="text-xs">
          Tentative (not fixed)
        </Label>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Payment Method</Label>
        <Controller
          name="paymentMethod"
          control={form.control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value || ""}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="handover">Handover</SelectItem>
                <SelectItem value="direct_to_office">
                  Direct to Office
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>
    </div>
  );
}

function InstallmentTab({ form }) {
  const [notFixed, setNotFixed] = useState(false);
  const frequency = form.watch("frequency");

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Installment Count</Label>
        <div className="flex items-center gap-3">
          {!notFixed && (
            <Input
              type="number"
              min={1}
              {...form.register("installmentCount")}
              placeholder="Number of installments"
              className="flex-1 h-8 text-xs"
            />
          )}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="notFixed"
              checked={notFixed}
              onCheckedChange={(checked) => {
                setNotFixed(checked);
                if (checked) {
                  form.setValue("installmentCount", "not_fixed");
                } else {
                  form.setValue("installmentCount", "");
                }
              }}
            />
            <Label htmlFor="notFixed" className="text-xs whitespace-nowrap">
              Not Fixed
            </Label>
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Frequency</Label>
        <Controller
          name="frequency"
          control={form.control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value || ""}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="bi_monthly">Bi-Monthly</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {frequency === "custom" && (
        <div className="space-y-1.5">
          <Label className="text-xs">Custom Frequency (days)</Label>
          <Input
            type="number"
            min={1}
            {...form.register("customFrequencyDays", { valueAsNumber: true })}
            placeholder="Number of days"
            className="h-8 text-xs"
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label className="text-xs">Start Date</Label>
        <Controller
          name="startDate"
          control={form.control}
          render={({ field }) => (
            <DatePicker
              selected={field.value}
              onSelect={field.onChange}
              placeholder="Pick start date"
              className="w-full"
            />
          )}
        />
      </div>
    </div>
  );
}

function FlexibleTab({ form }) {
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Next Check-In Date</Label>
        <Controller
          name="nextCheckInDate"
          control={form.control}
          render={({ field }) => (
            <DatePicker
              selected={field.value}
              onSelect={field.onChange}
              placeholder="Pick check-in date"
              className="w-full"
            />
          )}
        />
      </div>
    </div>
  );
}

function NoContactTab({ form }) {
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Call Status</Label>
        <Controller
          name="callStatus"
          control={form.control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value || ""}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="call_not_taken">Call Not Taken</SelectItem>
                <SelectItem value="unreachable">Unreachable</SelectItem>
                <SelectItem value="no_whatsapp_reply">
                  No WhatsApp Reply
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Action Taken</Label>
        <Controller
          name="actionTaken"
          control={form.control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value || ""}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="left_voicemail">Left Voicemail</SelectItem>
                <SelectItem value="sent_whatsapp">Sent WhatsApp</SelectItem>
                <SelectItem value="will_retry">Will Retry</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>
    </div>
  );
}

// ── Main Modal ──

const DISPOSITION_STATUS_MAP = {
  full_payment: "awaiting_funds",
  installment: "active_installment",
  flexible: "nurture",
  no_contact: "unreachable",
};

// Inner form component - keyed by activeTab to force re-mount and re-initialize useForm with correct schema
function FollowUpForm({
  activeTab,
  sponsor,
  banner,
  sponsorshipId,
  onLogSuccess,
  onOpenChange,
  logMutation,
}) {
  const [remarks, setRemarks] = useState("");

  const form = useForm({
    resolver: zodResolver(schemas[activeTab]),
    defaultValues: {},
  });

  const onSubmit = (values) => {
    let details = {};

    if (activeTab === "full_payment") {
      details = {
        paymentTiming: {
          timingType: values.timingType,
          month: values.month,
          week: values.week,
          exactDate: toNoonUTC(values.exactDate),
          isTentative: values.isTentative,
        },
        paymentMethod: values.paymentMethod,
      };
    } else if (activeTab === "installment") {
      details = {
        installment: {
          count: values.installmentCount,
          frequency: values.frequency,
          customFrequencyDays: values.customFrequencyDays,
          startDate: toNoonUTC(values.startDate),
        },
      };
    } else if (activeTab === "flexible") {
      details = {
        nextCheckInDate: toNoonUTC(values.nextCheckInDate),
      };
    } else if (activeTab === "no_contact") {
      details = {
        noContact: {
          callStatus: values.callStatus,
          actionTaken: values.actionTaken,
        },
      };
    }

    logMutation.mutate(
      {
        sponsorshipId,
        dispositionCategory: activeTab,
        details,
        remarks,
      },
      {
        onSuccess: () => {
          form.reset({});
          setRemarks("");
          onOpenChange(false);
          onLogSuccess?.({
            _id: sponsorshipId,
            followUp: { status: DISPOSITION_STATUS_MAP[activeTab] },
          });
        },
      },
    );
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="mt-3">
      {/* Banner */}
      <div
        className={`rounded-md border px-3 py-2 mb-4 text-xs ${banner.bg}`}
      >
        {banner.text}
      </div>

      <TabsContent value="full_payment" className="mt-0">
        <FullPaymentTab form={form} />
      </TabsContent>

      <TabsContent value="installment" className="mt-0">
        {sponsor?.followUp?.instalmentPlan?.isActive && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 mb-4">
            <p className="text-xs font-semibold text-amber-700 mb-1">
              Active instalment plan exists
            </p>
            <p className="text-xs text-amber-600">
              This sponsor already has an instalment plan with{" "}
              {
                sponsor.followUp.instalmentPlan.instalments.filter(
                  (i) => i.status === "pending" || i.status === "missed",
                ).length
              }{" "}
              instalment(s) remaining. Submitting a new plan will replace
              it.
            </p>
          </div>
        )}
        <InstallmentTab form={form} />
      </TabsContent>

      <TabsContent value="flexible" className="mt-0">
        <FlexibleTab form={form} />
      </TabsContent>

      <TabsContent value="no_contact" className="mt-0">
        <NoContactTab form={form} />
      </TabsContent>

      {/* Shared remarks */}
      <Separator className="my-4" />
      <div className="space-y-1.5">
        <Label className="text-xs">Remarks (optional)</Label>
        <Textarea
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Add any notes about this call..."
          rows={2}
          className="text-xs resize-none"
        />
      </div>

      {/* Form errors */}
      {Object.values(form.formState.errors).length > 0 && (
        <p className="text-xs text-destructive mt-2">
          Please fill in all required fields.
        </p>
      )}

      <Button
        type="submit"
        disabled={logMutation.isPending}
        className="w-full mt-4"
      >
        {logMutation.isPending && (
          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
        )}
        {logMutation.isPending ? "Submitting..." : "Submit Follow-Up"}
      </Button>
    </form>
  );
}

export function LogFollowUpModal({
  open,
  onOpenChange,
  sponsorshipId,
  sponsor,
  onLogSuccess,
}) {
  const [activeTab, setActiveTab] = useState("full_payment");
  const logMutation = useLogFollowUp();

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const sponsorName = sponsor?.sponsorName || "";
  const sponsorContext =
    sponsor?.type === "Mahallu"
      ? sponsor?.mahalluName || ""
      : sponsor?.place || "";
  const attemptNum = (sponsor?.followUp?.failedAttempts || 0) + 1;

  const banner = tabBanners[activeTab];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto p-4 pt-6">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-base">Log Follow-Up</DialogTitle>
          {sponsorName && (
            <DialogDescription className="text-xs">
              {sponsorName}
              {sponsorContext ? ` · ${sponsorContext}` : ""}
              {` · Attempt #${attemptNum}`}
            </DialogDescription>
          )}
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-4 h-8">
            <TabsTrigger
              value="full_payment"
              className="text-[10px] sm:text-xs"
            >
              💰 Full Payment
            </TabsTrigger>
            <TabsTrigger value="installment" className="text-[10px] sm:text-xs">
              📆 Installments
            </TabsTrigger>
            <TabsTrigger value="flexible" className="text-[10px] sm:text-xs">
              🤝 Flexible
            </TabsTrigger>
            <TabsTrigger value="no_contact" className="text-[10px] sm:text-xs">
              📵 No Contact
            </TabsTrigger>
          </TabsList>

          <FollowUpForm
            key={activeTab}
            activeTab={activeTab}
            sponsor={sponsor}
            banner={banner}
            sponsorshipId={sponsorshipId}
            onLogSuccess={onLogSuccess}
            onOpenChange={onOpenChange}
            logMutation={logMutation}
          />
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
